<template>
  <PrimeTable
    v-bind.prop="props"
    :pt="passThrough"
    scrollable
    :scrollHeight="scrollHeight ?? 'flex'"
    v-on="listeners"
    class="meridia_table"
  >
    <template v-if="!hideHeader" #header>
      <div
        class="w-full inline-flex justify-between p-m"
        :class="getPtClass < TablePassThrough > ('headerWrapper', passThrough)"
      >
        <div v-if="headerTitle" class="inline-flex items-center mr-sm">
          <div
            class="mr-sm text-black text-title-medium"
            :class="
              getPtClass < TablePassThrough > ('headerTitle', passThrough)
            "
          >
            {{ headerTitle }}
          </div>
          <MeridiaBadge
            :value="totalRecords"
            :type="'success'"
            :class="
              getPtClass < TablePassThrough > ('headerBadge', passThrough)
            "
          />
        </div>
        <div
          class="inline-flex items-center gap-m"
          :class="
            getPtClass < TablePassThrough > ('headerContainer', passThrough)
          "
        >
          <slot name="header" />
          <MeridiaMultipleSelect
            v-if="columnHiding"
            v-model="hiddenColumns"
            :options="columnsOptions"
            :content="'chips'"
            :selectionLimit="4"
            :allSelectDisabled="true"
            placeholder="Select to hide columns"
            class="w-64"
          />
          <slot name="headerBtn" />
        </div>
      </div>
    </template>
    <template v-if="slots.default">
      <MeridiaColumnGroup v-if="columnGroups" type="header">
        <!-- Column groups -->
        <MeridiaRow>
          <template
            v-for="(child, index) in columnGroups"
            :key="child.props.field + index"
          >
            <component
              :is="child"
              :colspan="
                columnHiding
                  ? getCurrentColumnSpanOfGroup(index)
                  : child.props.colspan
              "
              :hidden="
                columnHiding
                  ? getCurrentColumnSpanOfGroup(index) === 0
                  : child.props.colspan === 0
              "
            />
          </template>
        </MeridiaRow>
        <MeridiaRow>
          <!-- This will spread the columns so we can work with hiding columns and custom slots -->
          <template
            v-for="(child, index) in columns"
            :key="child.props.field + index"
          >
            <component
              :is="child"
              :frozen="frozenColumnsState[child.props.field]"
              :alignFrozen="
                Object.keys(frozenColumnsState).indexOf(child.props.field) <=
                Object.keys(frozenColumnsState).length / 2
                  ? 'left'
                  : 'right'
              "
              :style="{
                maxWidth: child.props?.maxWidth
                  ? `${child.props.maxWidth}px`
                  : '',
                minWidth: child.props?.minWidth
                  ? `${child.props.minWidth}px`
                  : '',
                overflow: child.props?.overflow ? 'auto' : '',
              }"
            >
              <template #header="{ column }">
                <div
                  v-if="showFreeze && column.props.header"
                  class="freeze-icon"
                  :class="[
                    {
                      unfreezed:
                        !canNotFreezeWithMax(child.props.field) &&
                        !isColumnFreezed(child.props.field),
                      freezed:
                        !canNotFreezeWithMax(child.props.field) &&
                        isColumnFreezed(child.props.field),
                      'cursor-not-allowed text-neutral-text-disabled':
                        canNotFreezeWithMax(child.props.field),
                    },
                  ]"
                  @click.stop="
                    () => {
                      if (canNotFreezeWithMax(child.props.field)) return;
                      toogleFreezeColumn(child.props.field);
                    }
                  "
                >
                  <MeridiaIcon
                    class="icon-xs"
                    name="lock"
                    :type="
                      isColumnFreezed(child.props.field)
                        ? 'material-icons'
                        : 'material-icons-outlined'
                    "
                  />
                </div>
              </template>

              <template #sorticon="{ sorted, sortOrder }">
                <div
                  class="sort-icon"
                  :class="[
                    !isColumnFreezed(child.props.field)
                      ? sorted
                        ? 'sorted'
                        : 'unsorted'
                      : 'bg-neutral-surface-disabled text-neutral-text-subtitle',
                  ]"
                >
                  <MeridiaIcon
                    v-if="sortOrder == 0"
                    class="icon-xs"
                    name="unfold_more"
                    type="material-icons-outlined"
                  />
                  <MeridiaIcon
                    v-else-if="sortOrder == 1"
                    class="icon-xs"
                    name="arrow_downward"
                    type="material-icons-outlined"
                  />
                  <MeridiaIcon
                    v-else-if="sortOrder == -1"
                    class="icon-xs"
                    name="arrow_upward"
                    type="material-icons-outlined"
                  />
                </div>
              </template>

              <template v-if="child?.children?.body" v-slot:body="slotData">
                <div
                  class="flex justify-center"
                  :class="[
                    child.props.field ===
                    columns[columns.length - 1].props.field
                      ? 'w-full'
                      : 'w-fit',
                    {
                      relative: loading,
                    },
                  ]"
                >
                  <div
                    v-if="loading"
                    class="absolute top-0 left-0 w-full h-full flex items-center justify-center"
                  >
                    <PrimeSkeleton class="!w-full !h-full" />
                  </div>
                  <div
                    :class="loading ? 'invisible' : 'visible'"
                    class="w-full"
                  >
                    <component :is="child.children['body']" v-bind="slotData" />
                  </div>
                </div>
              </template>

              <!-- This will spread the column slots so we can also add the custom header -->
              <template
                v-for="name in child?.children
                  ? Object.keys(child.children).filter(
                      (key) => !['body'].includes(key)
                    )
                  : []"
                :key="name"
                v-slot:[name]="slotData"
              >
                <component :is="child.children[name]" v-bind="slotData" />
              </template>
            </component>
          </template>
        </MeridiaRow>
      </MeridiaColumnGroup>

      <template
        v-for="(child, index) in columns"
        :key="child.props.field + index"
      >
        <component
          :is="child"
          :frozen="frozenColumnsState[child.props.field]"
          :alignFrozen="
            Object.keys(frozenColumnsState).indexOf(child.props.field) <=
            Object.keys(frozenColumnsState).length / 2
              ? 'left'
              : 'right'
          "
          :style="{
            maxWidth: child.props?.maxWidth ? `${child.props.maxWidth}px` : '',
            minWidth: child.props?.minWidth ? `${child.props.minWidth}px` : '',
            overflow: child.props?.overflow ? 'auto' : '',
          }"
          v-bind.prop="child.props"
        >
          <template #header="{ column }">
            <div
              v-if="showFreeze && column.props.header"
              class="freeze-icon"
              :class="[
                {
                  unfreezed:
                    !canNotFreezeWithMax(child.props.field) &&
                    !isColumnFreezed(child.props.field),
                  freezed:
                    !canNotFreezeWithMax(child.props.field) &&
                    isColumnFreezed(child.props.field),
                  'cursor-not-allowed text-neutral-text-disabled':
                    canNotFreezeWithMax(child.props.field),
                },
              ]"
              @click.stop="
                () => {
                  if (canNotFreezeWithMax(child.props.field)) return;
                  toogleFreezeColumn(child.props.field);
                }
              "
            >
              <MeridiaIcon
                class="icon-xs"
                name="lock"
                :type="
                  isColumnFreezed(child.props.field)
                    ? 'material-icons'
                    : 'material-icons-outlined'
                "
              />
            </div>
          </template>

          <template #sorticon="{ sorted, sortOrder }">
            <div
              class="sort-icon"
              :class="[
                !isColumnFreezed(child.props.field)
                  ? sorted
                    ? 'sorted'
                    : 'unsorted'
                  : 'bg-neutral-surface-disabled text-neutral-text-subtitle',
              ]"
            >
              <MeridiaIcon
                v-if="sortOrder == 0"
                class="icon-xs"
                name="unfold_more"
                type="material-icons-outlined"
              />
              <MeridiaIcon
                v-else-if="sortOrder == 1"
                class="icon-xs"
                name="arrow_downward"
                type="material-icons-outlined"
              />
              <MeridiaIcon
                v-else-if="sortOrder == -1"
                class="material-icons-outlined icon-xs"
                name="arrow_upward"
                type="material-icons-outlined"
              />
            </div>
          </template>

          <template v-if="child?.children?.body" v-slot:body="slotData">
            <div
              class="flex justify-center"
              :class="[
                child.props.field === columns[columns.length - 1].props.field
                  ? 'w-full'
                  : 'w-fit',
                {
                  relative: loading,
                },
              ]"
            >
              <div
                v-if="loading"
                class="absolute top-0 left-0 w-full h-full flex items-center justify-center"
              >
                <PrimeSkeleton class="!w-full !h-full" />
              </div>
              <div :class="loading ? 'invisible' : 'visible'" class="w-full">
                <component :is="child.children['body']" v-bind="slotData" />
              </div>
            </div>
          </template>

          <!-- This will spread the column slots so we can also add the custom header -->
          <template
            v-for="name in child?.children
              ? Object.keys(child.children).filter(
                  (key) => !['body'].includes(key)
                )
              : []"
            :key="name"
            v-slot:[name]="slotData"
          >
            <component :is="child.children[name]" v-bind="slotData" />
          </template>
        </component>
      </template>
    </template>
    <template #empty>
      <div class="w-full justify-center flex py-md px-sm">
        <div v-if="error">
          <ErrorInline
            :title="$t('datasets.list.error_table_data')"
            type="error"
            :style="'text-medium'"
            :iconSizePx="24"
          />
        </div>
        <div v-else-if="loading">Loading...</div>
        <div v-else>
          <ErrorInline
            :title="$t('datasets.list.no_table_data')"
            type="info"
            :style="'text-medium'"
            :iconSizePx="24"
          />
        </div>
      </div>
    </template>
    <template #expansion="slotData">
      <div
        v-if="loading"
        class="bg-slate-100 border-l-[3px] border-primary-border-default"
      >
        <table class="w-full">
          <tbody>
            <tr
              v-for="(parentDataset, index) in slotData.data.parent_datasets"
              :key="index"
              class="border-b border-slate-200 last:border-b-0"
            >
              <td class="p-2.5">
                <PrimeSkeleton class="!w-full !h-4" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <slot v-else name="expansion" v-bind="slotData || {}"></slot>
    </template>
    <template
      v-for="name in Object.keys(slots).filter(
        (key) =>
          !['default', 'header', 'columnGroups', 'expansion'].includes(key)
      )"
      :key="name"
      v-slot:[name]="slotData"
    >
      <slot :name="name" v-bind="slotData || {}"></slot>
    </template>
  </PrimeTable>
</template>

<script setup lang="ts" generic="T">
import { PassThrough } from '@primevue/core';
import { usePassThrough } from 'primevue/passthrough';
import ErrorInline from 'src/core/components/error/ErrorInline.vue';
import MeridiaBadge from '../../badge/MeridiaBadge.vue';
import {
  TableProps,
  TableSlots,
  TablePassThrough,
  TableEmitsOptions,
} from './MeridiaTable';
import { getPtClass, useListeners } from '../../utils';
import { ColumnProps } from 'primevue/column';
import {
  DataTableCellEditCancelEvent,
  DataTableCellEditCompleteEvent,
  DataTableCellEditInitEvent,
  DataTableColumnReorderEvent,
  DataTableColumnResizeEndEvent,
  DataTableEditingRows,
  DataTableExpandedRows,
  DataTableFilterEvent,
  DataTableFilterMeta,
  DataTablePageEvent,
  DataTableRowClickEvent,
  DataTableRowCollapseEvent,
  DataTableRowContextMenuEvent,
  DataTableRowDoubleClickEvent,
  DataTableRowEditCancelEvent,
  DataTableRowEditInitEvent,
  DataTableRowEditSaveEvent,
  DataTableRowExpandEvent,
  DataTableRowReorderEvent,
  DataTableRowSelectAllEvent,
  DataTableRowSelectEvent,
  DataTableRowUnselectAllEvent,
  DataTableRowUnselectEvent,
  DataTableSelectAllChangeEvent,
  DataTableSortEvent,
  DataTableSortMeta,
  DataTableStateEvent,
} from 'primevue/datatable';
import PrimeTable from 'primevue/datatable';
import { useTableResize } from './utils/useTableResize';
import { useColumns } from './utils/useColumns';
import { useColumnFreezing } from './utils/useColumnFreezing';
import PrimeSkeleton from 'primevue/skeleton';
import MeridiaMultipleSelect from '../../select/multipleSelect/MeridiaMultipleSelect.vue';
import MeridiaColumnGroup from '../columnGroup/MeridiaColumnGroup.vue';
import MeridiaRow from '../row/MeridiaRow.vue';
import { MeridiaPaginatorPassThroughValues } from '../../paginator/types';
import MeridiaIcon from '../../icon/MeridiaIcon.vue';

const props = withDefaults(defineProps<TableProps<T>>(), {
  hideHeader: false,

  // Data
  value: null,
  lazy: false,

  // Pagination
  paginator: false,
  rows: 0,
  first: 0,
  totalRecords: 0,
  paginatorPosition: 'bottom',
  alwaysShowPaginator: true,
  paginatorTemplate:
    'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown',
  pageLinkSize: 5,

  // Selection & Layout
  dataKey: null,
  selection: null,
  selectionMode: null,
  metaKeySelection: true,

  // Header & Footer
  header: null,
  footer: null,

  // Appearance
  rowHover: false,
  responsiveLayout: null,
  breakpoint: '960px',
  stripedRows: false,
  showGridlines: false,
  showHeaders: true,

  // Scrolling
  scrollable: false,
  scrollHeight: null,
  virtualScroll: false,
  virtualScrollDelay: 250,
  virtualScrollerOptions: null,

  // Sorting
  sortField: null,
  sortOrder: 1,
  multiSortMeta: null,
  removableSort: false,
  sortMode: 'single',
  sortIcon: 'pi pi-sort',
  sortIconAscending: 'pi pi-sort-up',
  sortIconDescending: 'pi pi-sort-down',

  // Filtering
  filters: null,
  filterMode: 'lenient',
  filterMatchModeOptions: null,

  // Row Styling & Grouping
  rowClass: null,
  rowGroupMode: null,
  groupRowsBy: null,
  rowGroupHeaderTemplate: null,
  rowGroupFooterTemplate: null,
  expandedRows: null,
  expandableRowGroups: false,

  // Editing
  editMode: 'cell',
  editingRows: null,

  // Loading State
  loading: false,
  loadingIcon: 'pi pi-spinner',

  // Columns
  resizableColumns: false,
  columnResizeMode: 'fit',
  reorderableColumns: false,

  // State Storage
  stateStorage: null,
  stateKey: null,

  // Paginator Dropdown
  paginatorDropdownAppendTo: 'self',
  paginatorDropdownScrollHeight: '200px',

  // Other
  stickyHeader: false,
  emptyMessage: 'No records found',
});
const slots = defineSlots<TableSlots<T>>();
const emit = defineEmits({
  'update:first': (value: number) => typeof value === 'number',
  'update:rows': (value: number) => typeof value === 'number',
  'update:sortField': (value: string) => typeof value === 'string',
  'update:sortOrder': (value: number | undefined) =>
    typeof value === 'number' || typeof value === 'undefined',
  'update:multiSortMeta': (value: DataTableSortMeta[] | undefined | null) =>
    true,
  'update:selection': (value: any[] | any | undefined) => true,
  'update:contextMenuSelection': (value: any[] | any | undefined) => true,
  'update:expandedRows': (value: any[] | DataTableExpandedRows) => true,
  'update:expandedRowGroups': (value: any[] | DataTableExpandedRows) => true,
  'update:filters': (value: DataTableFilterMeta) => true,
  'update:editingRows': (value: any[] | DataTableEditingRows) => true,
  page: (value: DataTablePageEvent) => true,
  sort: (value: DataTableSortEvent) => true,
  filter: (value: DataTableFilterEvent) => true,
  'value-change': (value: any[]) => true,
  'row-click': (value: DataTableRowClickEvent) => true,
  'row-dblclick': (value: DataTableRowDoubleClickEvent) => true,
  'row-contextmenu': (value: DataTableRowContextMenuEvent) => true,
  'row-select': (value: DataTableRowSelectEvent) => true,
  'row-select-all': (value: DataTableRowSelectAllEvent) => true,
  'row-unselect-all': (value: DataTableRowUnselectAllEvent) => true,
  'row-unselect': (value: DataTableRowUnselectEvent) => true,
  'select-all-change': (value: DataTableSelectAllChangeEvent) => true,
  'column-resize-end': (value: DataTableColumnResizeEndEvent) => true,
  'column-reorder': (value: DataTableColumnReorderEvent) => true,
  'row-reorder': (value: DataTableRowReorderEvent) => true,
  'row-expand': (value: DataTableRowExpandEvent) => true,
  'row-collapse': (value: DataTableRowCollapseEvent) => true,
  'rowgroup-expand': (value: DataTableRowExpandEvent) => true,
  'rowgroup-collapse': (value: DataTableRowCollapseEvent) => true,
  'cell-edit-init': (value: DataTableCellEditInitEvent) => true,
  'cell-edit-complete': (value: DataTableCellEditCompleteEvent) => true,
  'cell-edit-cancel': (value: DataTableCellEditCancelEvent) => true,
  'row-edit-init': (value: DataTableRowEditInitEvent) => true,
  'row-edit-save': (value: DataTableRowEditSaveEvent) => true,
  'row-edit-cancel': (value: DataTableRowEditCancelEvent) => true,
  'state-restore': (value: DataTableStateEvent) => true,
  'state-save': (value: DataTableStateEvent) => true,
});

const { listeners } = useListeners<TableEmitsOptions>(emit, PrimeTable);
const { generatedTableID, showFreeze } = useTableResize(props.columnFreezing);

const passThrough = usePassThrough(
  props.pt,
  {
    column: {
      pcColumnFilterButton: {
        class: 'hidden',
      },
      columnheadercontent: {
        class: 'gap-2xs',
      },
      columnTitle: {
        class:
          'text-neutral-text-body text-caption-medium uppercase whitespace-nowrap truncate overflow-hidden',
      },
      headerCell: ({
        props,
        instance,
      }: {
        props: ColumnProps;
        instance: any;
      }) => {
        if (
          instance &&
          'filterColumn' in instance &&
          instance.filterColumn === false
        ) {
          const index = columns.value.findIndex(
            (column) => column.props.field === props.field
          );
          return {
            class: {
              'header-cell border-neutral-border-default': true,
              'bg-neutral-surface-default': !props.frozen,
              'bg-neutral-surface-disabled': props.frozen,
              'py-s pl-m pr-xs border-b border-l': index === 0,
              'py-s px-xs border-b border-l':
                index > 0 && index < columns.value.length - 1,
              'py-s pr-m pl-xs border-b border-x':
                index === columns.value.length - 1,
            },
          };
        } else {
          return {
            class: {
              'bg-neutral-surface-default': !props.frozen,
              'bg-neutral-surface-disabled': props.frozen,
              'p-xs': true,
            },
          };
        }
      },
      bodyCell: ({ props }: { props: ColumnProps }) => {
        const index = columns.value.findIndex(
          (column) => column.props.field === props.field
        );
        return {
          class: {
            'bg-neutral-surface-default': !props.frozen,
            '!bg-neutral-surface-disabled': props.frozen,
            'whitespace-nowrap overflow-hidden truncate  border-neutral-border-default':
              true,
            'py-2.5 pl-m pr-xs border-l': index === 0,
            'py-2.5 px-xs': index > 0 && index < columns.value.length - 1,
            'py-2.5 pr-m pl-xs border-r': index === columns.value.length - 1,
          },
        };
      },
    },
    rowGroupHeaderCell: {
      class: 'p-0',
    },
    mask: {
      class: 'hidden',
    },
    tableContainer: {
      class: generatedTableID.value,
    },
    header: {
      class: ['p-0'],
    },
    virtualScroller: {
      root: {
        class: '!contain-none',
      },
    },
    pcPaginator: props.pt?.pcPaginator ?? MeridiaPaginatorPassThroughValues,
  } as TablePassThrough,
  { mergeSections: false, mergeProps: true }
) as PassThrough<TablePassThrough>;

const {
  hiddenColumns,
  columnsOptions,
  columns,
  columnGroups,
  getCurrentColumnSpanOfGroup,
} = useColumns<T>(slots);
const {
  frozenColumnsState,
  canNotFreezeWithMax,
  isColumnFreezed,
  toogleFreezeColumn,
} = useColumnFreezing(columns, showFreeze);
</script>

<style lang="scss">
.meridia_table {
  .sort-icon {
    @apply p-2xs items-center flex radius-full cursor-pointer transition-all duration-300;
    &.sorted {
      @apply bg-neutral-surface-disabled text-neutral-text-subtitle;
      &:active {
        @apply bg-[#33415533];
      }
    }
    &.unsorted {
      @apply bg-neutral-surface-default text-neutral-text-caption;
      &:active {
        @apply bg-neutral-surface-disabled text-neutral-text-subtitle;
      }
    }
  }

  .freeze-icon {
    @apply order-3 ml-auto p-2xs items-center flex radius-full cursor-pointer transition-all duration-300;
    &.freezed {
      @apply bg-neutral-surface-disabled text-neutral-text-subtitle;
      &:hover {
        @apply bg-[#3341551A];
      }
      &:active {
        @apply bg-[#33415533];
      }
    }
    &.unfreezed {
      @apply bg-neutral-surface-default text-neutral-text-caption;
      &:hover {
        @apply bg-neutral-surface-muted text-neutral-text-disabled;
      }
      &:active {
        @apply bg-neutral-surface-disabled text-neutral-text-subtitle;
      }
    }
  }

  .header-cell:hover:not(:has(.freeze-icon:hover)) .sort-icon {
    &.sorted {
      @apply bg-[#3341551A];
    }
    &.unsorted {
      @apply bg-neutral-surface-muted text-neutral-text-disabled;
    }
  }
}
</style>
